export const newTempId = () =>
  `tmp-${(window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`)}`

export function toDraftRow(item) {
  const v = item.currentVersion || {}
  return {
    key: item.id,
    id: item.id,
    tempId: null,
    title: item.title,
    description: item.description || null,
    contentType: item.contentType,
    documentId: v.documentId || null,
    documentName: v.documentName || null,
    html: v.html || null,
    videoDurationSeconds: v.videoDurationSeconds ?? null,
    versionNumber: v.versionNumber ?? null,
    audienceAll: !!item.audienceAll,
    companyIds: item.companyIds || [],
    changeNote: null,
    retired: false,
    contentChanged: false,
    requiresReview: true,
    counts: item.counts || { reviewed: 0, applicable: 0 },
  }
}

export function initDraft(serverItems) {
  const server = (serverItems || []).filter((i) => i.status === "active").map(toDraftRow)
  return { server, rows: server.map((r) => ({ ...r })) }
}

const mapRow = (rows, key, fn) => rows.map((r) => (r.key === key ? fn(r) : r))

export function draftReducer(state, action) {
  switch (action.type) {
    case "reset":
      return initDraft(action.items)
    case "discard":
      return { ...state, rows: state.server.map((r) => ({ ...r })) }
    case "add": {
      const tempId = action.row.tempId || newTempId()
      const row = {
        key: tempId,
        id: null,
        tempId,
        title: action.row.title,
        description: action.row.description || null,
        contentType: action.row.contentType,
        documentId: action.row.documentId || null,
        documentName: action.row.documentName || null,
        html: action.row.html || null,
        videoDurationSeconds: action.row.videoDurationSeconds ?? null,
        versionNumber: null,
        audienceAll: !!action.row.audienceAll,
        companyIds: action.row.companyIds || [],
        changeNote: action.row.changeNote || null,
        retired: false,
        contentChanged: true,
        requiresReview: true,
        counts: { reviewed: 0, applicable: 0 },
      }
      return { ...state, rows: [...state.rows, row] }
    }
    case "edit":
      return {
        ...state,
        rows: mapRow(state.rows, action.key, (r) => ({
          ...r,
          ...action.patch,
          contentChanged: r.contentChanged || !!action.contentChanged,
        })),
      }
    case "retire":
      return {
        ...state,
        rows: state.rows.flatMap((r) => (r.key !== action.key ? [r] : r.tempId ? [] : [{ ...r, retired: true }])),
      }
    case "restore":
      return { ...state, rows: mapRow(state.rows, action.key, (r) => ({ ...r, retired: false })) }
    case "move": {
      const rows = [...state.rows]
      const [moved] = rows.splice(action.from, 1)
      rows.splice(action.to, 0, moved)
      return { ...state, rows }
    }
    case "setRequiresReview":
      return { ...state, rows: mapRow(state.rows, action.key, (r) => ({ ...r, requiresReview: !!action.value })) }
    case "setAllRequiresReview":
      return {
        ...state,
        rows: state.rows.map((r) => (r.id && r.contentChanged ? { ...r, requiresReview: !!action.value } : r)),
      }
    default:
      return state
  }
}

const sameIds = (a, b) => [...(a || [])].sort().join(",") === [...(b || [])].sort().join(",")

function metaChanged(row, original) {
  return (
    row.title !== original.title ||
    (row.description || null) !== (original.description || null) ||
    row.audienceAll !== original.audienceAll ||
    !sameIds(row.companyIds, original.companyIds)
  )
}

const contentOf = (row) =>
  row.contentType === "html"
    ? { html: row.html }
    : { documentId: row.documentId, ...(row.contentType === "video" ? { videoDurationSeconds: row.videoDurationSeconds } : {}) }

export function buildOperations(state) {
  const serverById = new Map(state.server.map((r) => [r.key, r]))
  const ops = []

  for (const row of state.rows) {
    if (row.tempId) {
      ops.push({
        op: "create",
        tempId: row.tempId,
        title: row.title,
        description: row.description || null,
        contentType: row.contentType,
        documentId: row.contentType === "html" ? null : row.documentId,
        html: row.contentType === "html" ? row.html : null,
        videoDurationSeconds: row.contentType === "video" ? row.videoDurationSeconds : null,
        audienceAll: row.audienceAll,
        companyIds: row.audienceAll ? [] : row.companyIds,
        changeNote: row.changeNote || null,
      })
      continue
    }
    const original = serverById.get(row.key)
    if (!original) continue
    if (row.retired) {
      ops.push({ op: "retire", itemId: row.id })
      continue
    }
    if (!row.contentChanged && !metaChanged(row, original)) continue
    const op = {
      op: "update",
      itemId: row.id,
      title: row.title,
      description: row.description || null,
      audienceAll: row.audienceAll,
      companyIds: row.audienceAll ? [] : row.companyIds,
    }
    if (row.contentChanged) {
      op.content = contentOf(row)
      op.changeNote = row.changeNote || null
      op.requiresReview = row.requiresReview
    }
    ops.push(op)
  }

  const activeKeys = state.rows.filter((r) => !r.retired).map((r) => r.tempId || r.id)
  const existingInDraftOrder = activeKeys.filter((k) => serverById.has(k))
  const serverOrder = state.server.map((r) => r.key).filter((k) => existingInDraftOrder.includes(k))
  const reordered = existingInDraftOrder.join(",") !== serverOrder.join(",")
  const newBeforeExisting = activeKeys.some(
    (k, i) => !serverById.has(k) && activeKeys.slice(i + 1).some((n) => serverById.has(n))
  )
  if (reordered || newBeforeExisting) ops.push({ op: "reorder", order: activeKeys })
  return ops
}

export function describeChanges(state) {
  const ops = buildOperations(state)
  const rowsByKey = new Map(state.rows.map((r) => [r.tempId || r.id, r]))
  return ops.map((op) => {
    if (op.op === "reorder") return { key: "reorder", kind: "reorder", title: "Display order", contentChanged: false }
    const key = op.tempId || op.itemId
    const row = rowsByKey.get(key)
    return {
      key,
      kind: op.op === "create" ? "create" : op.op === "retire" ? "retire" : "edit",
      title: row?.title || "",
      contentChanged: op.op === "create" || !!op.content,
    }
  })
}

export function changedAudiences(state) {
  return buildOperations(state)
    .filter((op) => op.op === "create" || (op.op === "update" && op.content))
    .map((op) => ({ audienceAll: !!op.audienceAll, companyIds: op.companyIds || [] }))
}
